import { integer } from '../../lib/format.js';

/**
 * The pure half of `Combobox`.
 *
 * Split out because `manager-web` has no jsdom and no testing-library — every
 * test in this app is a `.ts` file over a plain function — so anything that
 * cannot be reached without rendering is, in practice, untested. The list
 * assembly, the wrap-around keyboard maths and the "what do we actually search
 * for" rule are the three places this control can be wrong in a way nobody
 * notices, so they live here and are covered in `combobox.logic.test.ts`.
 */

export interface ComboboxOption {
  id: string;
  /** The accessible name, and what goes in the box once it is chosen. */
  label: string;
  /** A second line — a SKU code, an outlet's region. Never the only identifier. */
  hint?: string;
}

/** The id of the "no selection" row. A filter offers one; a form must not. */
export const CLEAR_ID = '';

/**
 * The rows the listbox renders, which are NOT the rows the server returned.
 *
 * TWO ADDITIONS, both about a value the current page cannot show.
 *
 * `clearLabel` is the filter's "Semua SKU". Losing it was the real cost of
 * dropping `<select>`: on a native control "all" is an option you re-pick, and
 * a combobox with only an X button hides the way back to an unfiltered report.
 *
 * The pinned SELECTION is the editing case. A competition plan's SKU may be the
 * 900th by name while the list only ever holds twenty, so an operator opening
 * the picker on an existing plan would find their own choice missing — which
 * reads as the SKU having been deleted rather than as it being on page 45.
 */
export function listRows(
  options: readonly ComboboxOption[],
  value: ComboboxOption | null,
  clearLabel?: string,
): ComboboxOption[] {
  const rows: ComboboxOption[] = [];
  if (clearLabel) rows.push({ id: CLEAR_ID, label: clearLabel });
  if (value && !options.some((option) => option.id === value.id)) rows.push(value);
  rows.push(...options);
  return rows;
}

/**
 * Where the highlight goes next.
 *
 * `null` means "this key is not ours", and the caller must not `preventDefault`
 * a key it did not handle — swallowing every keystroke here is how a combobox
 * eats the Tab that was meant to leave it.
 *
 * WRAPS. Twenty rows in a seven-row window: ArrowUp from the first to reach the
 * last is one keystroke instead of nineteen, and there is no scroll position to
 * lose because the list is rebuilt on every settled search anyway.
 */
export function moveActive(key: string, active: number, count: number): number | null {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowDown':
      return (active + 1) % count;
    case 'ArrowUp':
      return (active - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/**
 * What to actually search for, which is not always what is in the box.
 *
 * After a pick the input holds the chosen row's label, and searching for it
 * costs a request whose only result is the row already selected — and then
 * REOPENING the picker shows a list of one, as though the catalogue had shrunk
 * to the thing you just chose. Text that still equals the selection is text the
 * user has not typed, so the term is empty and the list is the unfiltered first
 * page.
 */
export function searchTermFor(query: string, selectedLabel?: string): string {
  const trimmed = query.trim();
  if (trimmed === '') return '';
  return trimmed === selectedLabel?.trim() ? '' : trimmed;
}

/**
 * The footer. `null` when the page IS the whole list.
 *
 * This sentence is the point of the feature. The `<select>` this replaces
 * truncated at 200 and said nothing, so SKU 201 did not exist as far as the
 * screen was concerned. Twenty of 412 is still a truncation — but a visible one
 * with a stated way out, which is a different thing from a silent one.
 */
export function truncationNotice(shown: number, total: number): string | null {
  if (total <= shown) return null;
  return `Menampilkan ${shown} dari ${integer(total)} — persempit pencarian.`;
}

/**
 * The DOM id for a row, which `aria-activedescendant` needs to be unique.
 *
 * Keyed on the INDEX rather than on the option id, because `listRows` can pin a
 * selection above a page that also contains it in the ordinary case — and two
 * elements sharing an id means the screen reader announces whichever the
 * document happens to hit first.
 */
export function optionDomId(listId: string, index: number): string {
  return `${listId}-o${index}`;
}
